import React from 'react';
import * as FaIcons from 'react-icons/fa';
import { RichTextEditor, Toggle } from '../../../components/common';
import ImageField from './ImageField';
import { ICON_OPTIONS } from '../sectionTypes';
import './CmsField.css';

export const renderFaIcon = (name) => {
  const Icon = FaIcons[name];
  return Icon ? <Icon /> : <FaIcons.FaRegSquare />;
};

const NATIVE_INPUT_TYPES = ['number', 'date', 'email'];

const toOption = (option) =>
  typeof option === 'string'
    ? { value: option, label: option.charAt(0).toUpperCase() + option.slice(1).replace(/_/g, ' ') }
    : option;

/**
 * One CMS input, chosen by `type`. Every variant reports changes as a raw
 * value (not an event) so consumers stay declarative.
 */
const CmsField = ({
  type = 'text',
  label,
  value,
  onChange,
  placeholder,
  options = [],
  rows = 3,
  hint,
  error,
  required = false,
  disabled = false,
  folder = 'cms',
  id,
  inputProps = {},
}) => {
  const fieldId = id || `cms-field-${label?.toLowerCase().replace(/\s+/g, '-') || type}`;

  const labelNode = label ? (
    <label className="cf-label" htmlFor={fieldId}>
      {label}
      {required && <span className="cf-required">*</span>}
    </label>
  ) : null;

  const footNode = (
    <>
      {hint && !error && <span className="cf-hint">{hint}</span>}
      {error && <span className="cf-error">{error}</span>}
    </>
  );

  if (type === 'image') {
    return (
      <div className={`cms-field cf-type-image ${error ? 'has-error' : ''}`}>
        <ImageField
          label={label}
          value={value || ''}
          onChange={onChange}
          folder={folder}
          hint={hint}
          error={error}
          disabled={disabled}
        />
      </div>
    );
  }

  if (type === 'html') {
    return (
      <div className={`cms-field cf-type-html ${error ? 'has-error' : ''}`}>
        {labelNode}
        <RichTextEditor
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          minHeight={180}
        />
        {footNode}
      </div>
    );
  }

  if (type === 'boolean') {
    return (
      <div className={`cms-field cf-type-boolean ${error ? 'has-error' : ''}`}>
        <div className="cf-boolean-row">
          <Toggle
            name={fieldId}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            size="small"
          />
          <span className="cf-boolean-label">{label}</span>
        </div>
        {footNode}
      </div>
    );
  }

  if (type === 'icon') {
    const iconOptions = options.length ? options.map(toOption) : ICON_OPTIONS.map((name) => ({ value: name, label: name }));
    return (
      <div className={`cms-field cf-type-icon ${error ? 'has-error' : ''}`}>
        {labelNode}
        <div className="cf-icon-row">
          <div className="cf-select-wrap">
            <select
              id={fieldId}
              className="cf-select"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            >
              <option value="">No icon</option>
              {iconOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <span className="cf-select-arrow" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
          <span className="cf-icon-preview">{renderFaIcon(value)}</span>
        </div>
        {footNode}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className={`cms-field cf-type-select ${error ? 'has-error' : ''}`}>
        {labelNode}
        <div className="cf-select-wrap">
          <select
            id={fieldId}
            className="cf-select"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(toOption).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <span className="cf-select-arrow" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        {footNode}
      </div>
    );
  }

  if (type === 'textarea' || type === 'json') {
    return (
      <div className={`cms-field cf-type-${type} ${error ? 'has-error' : ''}`}>
        {labelNode}
        <textarea
          id={fieldId}
          className={`cf-textarea ${type === 'json' ? 'mono' : ''}`}
          rows={rows}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          spellCheck={type !== 'json'}
        />
        {footNode}
      </div>
    );
  }

  return (
    <div className={`cms-field cf-type-${type} ${error ? 'has-error' : ''}`}>
      {labelNode}
      <input
        id={fieldId}
        type={NATIVE_INPUT_TYPES.includes(type) ? type : 'text'}
        className="cf-input"
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        {...inputProps}
      />
      {footNode}
    </div>
  );
};

export default CmsField;
